import React, { useEffect, useRef, useState } from 'react';
import { VoiceOptions, VoiceSummary, defaultVoiceOptions } from './VoiceCalculator';
import { calculateVoiceQuote } from '../utils/voicePricing';
import { submitVoiceApplication } from '../utils/submitVoiceApplication';
import { prepareVoiceApplication } from '../utils/voiceApplication';
import VoiceQuoteDetails from './VoiceQuoteDetails';
import VoiceQuoteLink from './VoiceQuoteLink';
import { defaultAvailability } from '../utils/voiceQuoteLink';
import VoicePaymentFields from './VoicePaymentFields';

export function VoiceApplicationConfirmation({ submission }) {
  const heading = useRef(null);
  useEffect(() => { heading.current?.focus(); }, []);
  return <section className="jobs-contact voice-confirmation">
    <h1 ref={heading} tabIndex={-1}>送信しました</h1>
    <p role="status">応募を受け付けました。{submission.applicationId && `受付番号：${submission.applicationId}。`}担当者からの連絡をお待ちください。</p>
    <h2>送信した応募内容</h2>
    <div className="voice-submitted-content">{submission.text}</div>
    {submission.payment?.method === 'paypay' && <img className="voice-payment-qr" src={`data:${submission.payment.qr.contentType};base64,${submission.payment.qr.data}`} alt="送信したPayPay受け取り用QRコード" />}
    <div className="voice-actions"><a className="jobs-apply" href="/jobs">Job一覧に戻る</a></div>
  </section>;
}

export default function VoiceApplication({ job, options: sharedOptions, onOptionsChange, onSubmitted, shareUrl }) {
  const [localOptions, setLocalOptions] = useState(defaultVoiceOptions);
  const options = sharedOptions || localOptions;
  const setOptions = onOptionsChange || setLocalOptions;
  const localDate = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const today = localDate(new Date());
  const [localAvailability, setLocalAvailability] = useState(defaultAvailability);
  const availability = options.availability ?? localAvailability;
  const setAvailability = value => onOptionsChange ? setOptions({ ...options, availability: value }) : setLocalAvailability(value);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [mailFallback, setMailFallback] = useState(null);
  const [payment, setPayment] = useState({ method: 'later' });
  useEffect(() => { setMailFallback(null); setStatus(null); }, [options.rate, options.binaural, options.video, options.tip, options.withholding, availability, job.chapter]);
  let quote, quoteError;
  try { quote = calculateVoiceQuote(job.text, options); } catch (error) { quoteError = error.message; }
  async function submit(e) {
    e.preventDefault();
    if (busy || submission) return;
    const form = e.currentTarget;
    const fields = Object.fromEntries(new FormData(form));
    setBusy(true); setStatus(null); setMailFallback(null);
    try {
      const payload = { ...fields, chapter: job.chapter, binaural: options.binaural, video: options.video, rate: options.rate, tip: options.tip ?? 0, withholding: options.withholding ?? true, payment };
      const { text, payment: submittedPayment } = prepareVoiceApplication(payload);
      const result = await submitVoiceApplication(payload);
      if (!result.delivered && !result.accepted) {
        setMailFallback(result); setStatus(result.message); return;
      }
      const submitted = { text, payment: submittedPayment, applicationId: result.applicationId };
      setSubmission(submitted);
      onSubmitted?.(submitted);
    } catch (e) { setStatus(e.message === 'Failed to fetch' ? '通信に失敗しました。時間をおいて再試行してください。' : e.message); }
    finally { setBusy(false); }
  }
  if (submission) return <VoiceApplicationConfirmation submission={submission} />;
  return <section className="jobs-contact voice-form" id="application"><h2>この案件に応募する</h2><p>希望する単価と収録形式を選んでご応募ください。単価の初期値は1.5円です。変更した単価は希望条件として送信され、正式な報酬は依頼前に合意します。</p>
    <form onSubmit={submit} onChange={() => { setMailFallback(null); setStatus(null); }}><fieldset disabled={busy}><legend>応募内容</legend>
      <VoiceOptions options={options} onChange={setOptions} />
      {quote && <VoiceSummary quote={quote} />}
      {quoteError && <p role="alert">{quoteError}</p>}
      <label>お名前・活動名（必須）<input name="name" required maxLength="120" /></label>
      <label>メールアドレス（必須）<input name="email" type="email" required maxLength="255" /></label>
      <label>ボイスサンプルURL（必須）<input name="sample" type="url" required maxLength="1000" placeholder="https://" /></label>
      <label>納品可能日（必須・初期値は今日から1週間後）<input name="availability" type="date" required min={today} value={availability} onChange={e => setAvailability(e.target.value)} /></label>
      <label>自己紹介・収録環境・ご質問（必須）<textarea name="message" required rows="5" maxLength="3000" /></label>
      <VoicePaymentFields payment={payment} onChange={next => { setPayment(next); setMailFallback(null); setStatus(null); }} />
      <label><input type="checkbox" required name="consent" value="yes" /> 募集内容と<a href="/privacy" target="_blank" rel="noreferrer">プライバシーポリシー</a>を確認し、応募情報を選考・連絡に使用することに同意します。</label>
      <div className="voice-actions">
        <button type="submit" disabled={!quote}>{busy ? '送信中…' : 'この内容で応募する'}</button>
        <VoiceQuoteDetails job={job} quote={quote} availability={availability} />
        {shareUrl !== undefined && <VoiceQuoteLink url={shareUrl} />}
      </div>
    </fieldset></form>{status && <p role="status">{status}</p>}
    {mailFallback && <div className="voice-mail-fallback">
      <a className="jobs-apply" href={mailFallback.mailto}>入力内容をメールアプリで開く</a>
      <p>この時点では応募は送信されていません。メールアプリで送信すると応募できます。</p>
      {mailFallback.requiresQrAttachment && <p role="alert">PayPayのQRコード画像はメールアプリへ自動添付されません。選択した画像をメールに添付してから送信してください。</p>}
      <details><summary>メールアプリが開かない場合</summary><p>下の応募内容をコピーし、kei@sugano.works 宛てにメールを送ってください。</p><textarea aria-label="メール応募用の内容" readOnly rows="10" value={mailFallback.applicationText} onFocus={e => e.target.select()} /></details>
    </div>}
    <p>お問い合わせ：<a href="mailto:kei@sugano.works">kei@sugano.works</a></p>
  </section>;
}
